import { FC } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import {
  Button,
  Heading,
  Label,
  Types,
  IconCollection,
  IconCollectionAlignment,
  IconCollectionVariation,
  LogoContainer,
} from '@marriott/mi-ui-library';
import { BrandRibbonWrapper, StyledBrandRibbon } from './BrandRibbon.styles';
import { BrandRibbonProps } from './BrandRibbon.types';
import { LinkLists, BrandRibbonVariation } from './BrandRibbon.types';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import DOMPurify from 'isomorphic-dompurify';
import { BrandRibbonCategory } from '@marriott/mi-ui-library';

export const BrandRibbonComp: FC<BrandRibbonProps> = ({
  brandLogoTag,
  categorylists,
  componentId,
  ctaLink = '',
  forwardedRefs,
  header: headerUnsafe,
  logoLinkText = 'Marriott Bonvoy',
  trackingProperties,
  variations = BrandRibbonVariation.PortfolioRibbon,
  featureFlagLogo,
}: BrandRibbonProps) => {
  const customAttributes = trackingProperties?.enableScrollingBehavior ? { 'data-section-tracking': componentId } : {};

  const isLogoFlagEnabled = featureFlagLogo?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION];

  const showBrandRibbonVariation = variations === BrandRibbonVariation?.BrandRibbon && isLogoFlagEnabled;
  const showBrandRibbonVariationFallback = variations === BrandRibbonVariation?.BrandRibbon && !isLogoFlagEnabled;
  const showPortfolioRibbonVariation =
    variations === BrandRibbonVariation?.PortfolioRibbon || showBrandRibbonVariationFallback;
  const showPortfolioBrandsVariation =
    variations === BrandRibbonVariation?.PortfolioBrands ||
    (!showBrandRibbonVariation && !showPortfolioRibbonVariation);

  function cleanText(text: string | undefined): string {
    return text
      ? (DOMPurify.sanitize(text, { ALLOWED_TAGS: ['sub', 'sup'], RETURN_DOM: true }) as HTMLElement).innerHTML
      : '';
  }

  // flag needs to be replaced based on feature flag
  function portfolioRibbonToggle(isLogoFlagEnabled: boolean, list: LinkLists) {
    if (!isLogoFlagEnabled) {
      return (
        <IconCollection
          variation={IconCollectionVariation.LogoLinks}
          alignment={IconCollectionAlignment.Horizontal}
          iconCollectionData={list.linklists}
          trackingProperties={trackingProperties}
          forwardedRefs={forwardedRefs}
        />
      );
    } else {
      return (
        <ul className="p-0 linkCollectionItem horizontal-align">
          {list.linklists?.length &&
            list.linklists?.map((el, idx: number) => (
              <li className={'linkItems mr-3'} key={`${el}_${idx}`}>
                <LogoContainer
                  ref={forwardedRefs?.[idx]}
                  brand={el?.brandtag}
                  color={'background-aware'}
                  aspectRatio={'3:2'}
                  width={64}
                  height={42.67}
                  alignment={'Centered'}
                  isLink={true}
                  linkUrl={el.linkurl}
                  brandFullName={el.linkText}
                  target={el.openinanewtab ? '_blank' : '_self'}
                  aria-label={`${el.linkText ? el.linkText : el.brandtag} icon`}
                  tabIndex={0}
                />
              </li>
            ))}
        </ul>
      );
    }
  }

  // flag needs to be replaced based on feature flag
  function portfolioBrandsToggle(isLogoFlagEnabled: boolean, categorylists: LinkLists[]) {
    if (!isLogoFlagEnabled) {
      return (
        <IconCollection
          enableLinkClsName={BrandRibbonVariation.PortfolioBrands ? true : false}
          variation={IconCollectionVariation.LogoLinks}
          alignment={IconCollectionAlignment.Horizontal}
          iconCollectionData={categorylists?.[0].linklists}
          trackingProperties={trackingProperties}
          forwardedRefs={forwardedRefs}
        />
      );
    } else {
      return (
        <ul className="p-0 linkCollectionItem vertical-align">
          {categorylists?.[0].linklists.length &&
            categorylists?.[0].linklists?.map((el, idx) => (
              <li className={`linkItems svg-logo-${el.brandtag}`} key={`${el}_${idx}`}>
                <LogoContainer
                  ref={forwardedRefs?.[idx]}
                  brand={el?.brandtag}
                  color={'alternate-logo'}
                  aspectRatio={'3:2'}
                  width={90}
                  height={60}
                  alignment={'Centered'}
                  isLink={true}
                  linkUrl={el.linkurl}
                  brandFullName={el.linkText}
                  target={el.openinanewtab ? '_blank' : '_self'}
                  hoverColor={'#fff'}
                  aria-label={`${el.linkText ? el.linkText : el.brandtag} icon`}
                  tabIndex={0}
                />
              </li>
            ))}
        </ul>
      );
    }
  }

  return (
    <StyledBrandRibbon
      data-component-name="o-common-static-brandribbon"
      id={componentId}
      data-testid="brand-ribbon"
      className={clsx({
        'brand-ribbon-extra-pt': Boolean(brandLogoTag) && !showBrandRibbonVariation,
        'brand-ribbon-o-brand-portfolio': showBrandRibbonVariation,
      })}
      isLogoFlagEnabled={isLogoFlagEnabled}
      {...customAttributes}
    >
      {showBrandRibbonVariation && (
        <div className="brand-ribbon-full-container">
          <div className="brand-ribbon-logo-container" data-testid="brand-ribbon-header">
            <LogoContainer
              brand={brandLogoTag || 't-program-logo-color-s'}
              color={'color'}
              aspectRatio={'4:2'}
              width={87}
              height={43}
              alignment={'Centered'}
              isLink={true}
              linkUrl={ctaLink}
              brandFullName={logoLinkText}
              aria-label={logoLinkText}
              tabIndex={0}
            />
          </div>
          {headerUnsafe && (
            <Heading
              dangerouslySet={cleanText(headerUnsafe)}
              element={Types.tags.h2}
              variation={Types.headingType.title}
              fontSize={Types.size.small}
              customClass="brand-ribbon-header"
            />
          )}
          <div className="brand-ribbon-category-container">
            {categorylists?.map((list: LinkLists) => (
              <BrandRibbonCategory
                categorylabel={list.categorylabel?.toLocaleLowerCase()}
                description={cleanText(list.description)}
                linklists={list.linklists}
              />
            ))}
          </div>
        </div>
      )}
      {showPortfolioRibbonVariation && (
        <div className="brand-ribbon-inner-container">
          <div className="brand-ribbon-logo" data-testid="brand-ribbon-header">
            <Button isLink href={ctaLink} className={brandLogoTag} ariaLabel={logoLinkText}></Button>
          </div>
          {categorylists?.map((list: LinkLists) => (
            <div className="row" key={list.categorylabel}>
              <Label
                labelText={list.categorylabel?.toLocaleUpperCase()}
                element={Types.tags.h3}
                size={Types.size.extraSmall}
                customClass="category col-12 col-lg-1"
              />
              <div className="cardLinks col-12 col-lg-11" role="region">
                {portfolioRibbonToggle(isLogoFlagEnabled, list)}
              </div>
            </div>
          ))}
        </div>
      )}
      {showPortfolioBrandsVariation && (
        <div className="cardLinks navBrands" role="region" data-testid="portfolio-brands">
          {portfolioBrandsToggle(isLogoFlagEnabled, categorylists)}
        </div>
      )}
    </StyledBrandRibbon>
  );
};

export const BrandRibbonConfig = {
  emptyLabel: 'BrandRibbon',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/brandribbon`,
};

const BrandRibbonContainer = (props: BrandRibbonProps) => {
  const { styleclass } = props.model || {};

  return styleclass?.includes('fullbleed') ? (
    <BrandRibbonWrapper className={styleclass}>
      <div className="container">
        <BrandRibbonComp {...props} />
      </div>
    </BrandRibbonWrapper>
  ) : (
    <div className={clsx('container', styleclass)}>
      <BrandRibbonComp {...props} />
    </div>
  );
};

export const BrandRibbon = (props: BrandRibbonProps) => (
  <div data-testid="brand-ribbon-org">
    {props.cqPath?.includes('experiencefragment') ? (
      <BrandRibbonContainer {...props} {...props.model} />
    ) : (
      <EditableComponent config={BrandRibbonConfig} {...props}>
        <BrandRibbonContainer {...props} />
      </EditableComponent>
    )}
  </div>
);
